import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import {
  PAYMENT_METHODS,
  prefillAgreementFields,
  applyAcceptanceContactPrefill,
  fetchAcceptanceContactPrefill,
  liveContactFormValues,
  validateAgreementStep,
  confirmationFloorPlanAnswer,
  validateFloorPlanConfirmation,
  buildProductionSnapshot,
  buildAcceptanceRecord,
  paymentMethodLabel,
  formatEventDateRange,
  agreementConfirmationCopy,
  stripClientNameVersion,
  parseYmd,
  formatUsDate,
  inferRolloverDate,
  applyLinkedHardScheduleTimes,
  formatContextDate,
  resolveVenueTimeZone,
  formatScheduleTimeZoneLabel,
  formatPhoneInput,
  collectProductionChanges,
  ACCEPTANCE_RESOURCE_ACCEPT,
  coiStatusLabel,
  acceptanceResourceRejection,
  fetchProjectResources,
  uploadProjectResourceFile,
  removeProjectResourceFile,
} from '../lib/proposalAcceptance';
import {
  getMaykerRemittanceAddress,
  getWireInstructionsPdfUrl,
} from '../lib/maykerBusinessDetails';
import MaykerTimeInput from './MaykerTimeInput';

const INK = '#2C2C2C';
const MUTED = '#6b6b6b';
const RULE = '#e8e4dc';
const FONT = "'Neue Haas Unica', 'Inter', sans-serif";
const SERIF = "'Domaine Text', Georgia, serif";
const FLOOR_PLAN_ANSWER_OPTIONS = [
  { value: 'required', label: 'Yes, a floor plan is required' },
  { value: 'not_required', label: 'No, a floor plan is not required' },
];

function productionDetailsForProposal(proposal, clientProfile) {
  return {
    ...buildProductionSnapshot(proposal, clientProfile),
    floorPlanStatus: confirmationFloorPlanAnswer(proposal),
  };
}

function primaryBtn(overrides = {}) {
  return {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '10px 20px',
    fontSize: 12,
    fontWeight: 500,
    lineHeight: 1.25,
    borderRadius: '999px',
    cursor: 'pointer',
    fontFamily: FONT,
    boxSizing: 'border-box',
    whiteSpace: 'nowrap',
    backgroundColor: '#2C2C2C',
    color: '#ffffff',
    border: '1px solid transparent',
    ...overrides,
  };
}

function FieldLabel({ children, htmlFor, tight }) {
  return (
    <label htmlFor={htmlFor} style={{ display: 'block', fontSize: 13, fontWeight: 500, letterSpacing: 0, textTransform: 'none', color: INK, marginBottom: tight ? 0 : 8, fontFamily: FONT }}>
      {children}
    </label>
  );
}

function selectControlStyle(invalid) {
  return {
    width: '100%',
    boxSizing: 'border-box',
    padding: '11px 12px',
    minHeight: 44,
    border: `1px solid ${invalid ? '#8a6a5a' : RULE}`,
    borderRadius: 6,
    fontSize: 14,
    fontFamily: FONT,
    color: INK,
    backgroundColor: '#fff',
    outline: 'none',
    appearance: 'none',
    WebkitAppearance: 'none',
    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%236b6b6b' d='M6 9L1 4h10z'/%3E%3C/svg%3E")`,
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'right 12px center',
    paddingRight: 32,
  };
}

function TextInput({ id, value, onChange, type = 'text', invalid, disabled }) {
  return (
    <input
      id={id}
      className="mayker-field"
      type={type}
      value={value}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value)}
      style={{ width: '100%', boxSizing: 'border-box', padding: '11px 12px', minHeight: 44, border: `1px solid ${invalid ? '#8a6a5a' : RULE}`, borderRadius: 6, fontSize: 14, fontFamily: FONT, color: INK, backgroundColor: disabled ? '#fafaf8' : '#fff', outline: 'none' }}
    />
  );
}

function FieldError({ message }) {
  if (!message) return null;
  return <div style={{ marginTop: 6, fontSize: 12, color: '#7a5348', fontFamily: FONT }}>{message}</div>;
}

function SectionHeading({ children }) {
  return (
    <h3 style={{ margin: '0 0 12px', fontSize: 12, fontWeight: 500, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#8a8378', fontFamily: FONT }}>
      {children}
    </h3>
  );
}

function ReviewRow({ label, children }) {
  return (
    <div style={{ padding: '12px 0', borderBottom: `1px solid ${RULE}` }}>
      <div style={{ fontSize: 12, fontWeight: 500, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#8a8378', marginBottom: 6, fontFamily: FONT }}>{label}</div>
      <div style={{ fontSize: 14, fontWeight: 400, color: INK, fontFamily: FONT, lineHeight: 1.45 }}>{children}</div>
    </div>
  );
}

export default function AcceptProposalFlow({
  open,
  proposal,
  clientProfile = null,
  source = 'reserve',
  contactsPrefillUrl = '',
  onSubmit,
  onClose,
  onAccepted,
}) {
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [success, setSuccess] = useState(null);
  const submittedRef = useRef(false);
  const [liveContacts, setLiveContacts] = useState(null);
  const proposalKey = `${proposal && proposal.projectNumber ? proposal.projectNumber : ''}:${proposal && proposal.version != null ? proposal.version : ''}`;
  const folderLookup = {
    rwTicket: proposal && (proposal.rwTicketNumber || proposal.rwTicket),
    clientName: proposal && proposal.clientName,
    startDate: proposal && proposal.startDate,
    endDate: proposal && proposal.endDate,
    venueName: proposal && proposal.venueName,
    clientFolderURL: proposal && proposal.clientFolderURL,
  };
  const contactProposal = useMemo(
    () => (liveContacts ? applyAcceptanceContactPrefill(proposal, liveContacts) : proposal),
    [proposal, liveContacts]
  );
  const originalProduction = useMemo(() => buildProductionSnapshot(contactProposal, clientProfile), [contactProposal, clientProfile]);
  const prefill = useMemo(() => prefillAgreementFields(contactProposal, clientProfile), [contactProposal, clientProfile]);
  const [agreementCompanyName, setAgreementCompanyName] = useState(prefill.agreementCompanyName);
  const [agreementContactName, setAgreementContactName] = useState(prefill.agreementContactName);
  const [agreementEmail, setAgreementEmail] = useState(prefill.agreementEmail);
  const [preferredPaymentMethod, setPreferredPaymentMethod] = useState('');
  const [productionDetails, setProductionDetails] = useState(() => productionDetailsForProposal(proposal, clientProfile));
  const [floorPlanError, setFloorPlanError] = useState('');
  const [productionNotes, setProductionNotes] = useState('');
  const [existingResources, setExistingResources] = useState([]);
  const [sessionResources, setSessionResources] = useState([]);
  const [resourceError, setResourceError] = useState('');
  const [resourceUploading, setResourceUploading] = useState(false);
  const [step1Errors, setStep1Errors] = useState({});
  const timeValidatorsRef = useRef(new Map());
  const registerTimeValidator = (id, fn) => {
    timeValidatorsRef.current.set(id, fn);
    return () => {
      timeValidatorsRef.current.delete(id);
    };
  };
  const validateTimeFields = () => {
    let ok = true;
    timeValidatorsRef.current.forEach((fn) => {
      if (!fn()) ok = false;
    });
    return ok;
  };

  useEffect(() => {
    if (!open) {
      setLiveContacts(null);
      return;
    }
    const initialPrefill = prefillAgreementFields(proposal, clientProfile);
    setLiveContacts(null);
    setStep(1);
    setSubmitting(false);
    setSubmitError('');
    setSuccess(null);
    submittedRef.current = false;
    setAgreementCompanyName(initialPrefill.agreementCompanyName);
    setAgreementContactName(initialPrefill.agreementContactName);
    setAgreementEmail(initialPrefill.agreementEmail);
    setPreferredPaymentMethod('');
    setProductionDetails(productionDetailsForProposal(proposal, clientProfile));
    setFloorPlanError('');
    setProductionNotes('');
    setExistingResources([]);
    setSessionResources([]);
    setResourceError('');
    setResourceUploading(false);
    setStep1Errors({});
  }, [open, proposalKey]);

  useEffect(() => {
    if (!open || !proposal?.projectNumber || !contactsPrefillUrl) return undefined;
    let cancelled = false;
    fetchAcceptanceContactPrefill({
      url: contactsPrefillUrl,
      projectNumber: proposal.projectNumber,
      version: proposal.version,
      rwTicket: proposal.rwTicketNumber || proposal.rwTicket,
      clientName: proposal.clientName,
      startDate: proposal.startDate,
      endDate: proposal.endDate,
      venueName: proposal.venueName,
    }).then((contacts) => {
      if (cancelled || !contacts) return;
      setLiveContacts(contacts);
      const live = liveContactFormValues(contacts);
      if (live.agreementContactName) setAgreementContactName((cur) => cur || live.agreementContactName);
      if (live.agreementEmail) setAgreementEmail((cur) => cur || live.agreementEmail);
      if (live.dayOfContact || live.dayOfContactPhone) {
        setProductionDetails((cur) => ({
          ...cur,
          dayOfContact: cur.dayOfContact || live.dayOfContact,
          dayOfContactPhone: cur.dayOfContactPhone || live.dayOfContactPhone,
        }));
      }
    });
    return () => {
      cancelled = true;
    };
  }, [open, proposalKey, contactsPrefillUrl]);

  useEffect(() => {
    if (!open || !proposal?.projectNumber || !contactsPrefillUrl) return undefined;
    let cancelled = false;
    fetchProjectResources({
      url: contactsPrefillUrl,
      projectNumber: proposal.projectNumber,
      version: proposal.version,
      ...folderLookup,
    }).then((files) => {
      if (!cancelled) setExistingResources(files);
    });
    return () => {
      cancelled = true;
    };
  }, [open, proposalKey, contactsPrefillUrl]);

  useEffect(() => {
    if (!open || !liveContacts) return;
    setAgreementContactName((cur) => cur || prefill.agreementContactName);
    setAgreementEmail((cur) => cur || prefill.agreementEmail);
    setProductionDetails((cur) => ({
      ...cur,
      dayOfContact: cur.dayOfContact || originalProduction.dayOfContact,
      dayOfContactPhone: cur.dayOfContactPhone || originalProduction.dayOfContactPhone,
    }));
  }, [open, liveContacts, prefill.agreementContactName, prefill.agreementEmail, originalProduction.dayOfContact, originalProduction.dayOfContactPhone]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape' && !submitting) onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, submitting, onClose]);

  if (!open) return null;

  const remit = getMaykerRemittanceAddress();
  const wirePdfUrl = getWireInstructionsPdfUrl();
  const fields = { agreementCompanyName, agreementContactName, agreementEmail, preferredPaymentMethod };

  const readFileAsBase64 = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result || '');
      const comma = result.indexOf(',');
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.onerror = () => reject(reader.error || new Error('Could not read file'));
    reader.readAsDataURL(file);
  });

  const addResourceFiles = async (fileList, options) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    setResourceError('');
    setResourceUploading(true);
    try {
      for (const file of files) {
        const rejection = acceptanceResourceRejection(file);
        if (rejection) {
          setResourceError(rejection);
          continue;
        }
        const base64 = await readFileAsBase64(file);
        const result = await uploadProjectResourceFile({
          url: contactsPrefillUrl,
          projectNumber: proposal.projectNumber,
          version: proposal.version,
          ...folderLookup,
          file,
          base64,
        });
        if (!result.success || !result.file) {
          setResourceError(result.error || 'Could not upload that file.');
          continue;
        }
        setSessionResources((prev) => [...prev, { ...result.file, purpose: options && options.purpose === 'coi' ? 'coi' : result.file.purpose || '' }]);
      }
    } catch (err) {
      setResourceError(err.message || 'Could not upload that file.');
    } finally {
      setResourceUploading(false);
    }
  };

  const removeSessionResource = async (file) => {
    if (!file || !file.id) return;
    setResourceError('');
    const result = await removeProjectResourceFile({
      url: contactsPrefillUrl,
      projectNumber: proposal.projectNumber,
      version: proposal.version,
      ...folderLookup,
      fileId: file.id,
    });
    if (!result.success) {
      setResourceError(result.error || 'Could not remove that file.');
      return;
    }
    setSessionResources((prev) => prev.filter((item) => item.id !== file.id));
  };

  const goStep2 = () => {
    const errors = validateAgreementStep(fields);
    setStep1Errors(errors);
    if (Object.keys(errors).length) return;
    setStep(2);
  };

  const goToReview = () => {
    if (!validateTimeFields()) return;
    const floorErrors = validateFloorPlanConfirmation(productionDetails);
    setFloorPlanError(floorErrors.floorPlanStatus || '');
    if (floorErrors.floorPlanStatus) return;
    setStep(3);
  };

  const handleSubmit = async () => {
    if (submitting || submittedRef.current) return;
    const errors = validateAgreementStep(fields);
    if (Object.keys(errors).length) {
      setStep1Errors(errors);
      setStep(1);
      return;
    }
    const floorErrors = validateFloorPlanConfirmation(productionDetails);
    if (floorErrors.floorPlanStatus) {
      setFloorPlanError(floorErrors.floorPlanStatus);
      setStep(2);
      return;
    }
    submittedRef.current = true;
    setSubmitting(true);
    setSubmitError('');
    const venueTimeZone = resolveVenueTimeZone({ proposal, venueAddress: productionDetails.venueAddress });
    const acceptance = buildAcceptanceRecord({
      proposal,
      fields,
      productionDetails,
      productionNotes,
      supportingDocuments: sessionResources,
      eventTimeZone: venueTimeZone,
      submittedBy: {
        name: (clientProfile && (clientProfile.fullName || clientProfile.contactName)) || agreementContactName,
        email: (clientProfile && clientProfile.email) || agreementEmail,
      },
      source,
      originalProduction,
      originalAgreement: prefill,
    });
    try {
      const result = await onSubmit({
        versionId: proposal.versionId || proposal.id || '',
        projectNumber: proposal.projectNumber,
        version: proposal.version,
        proposalSnapshot: proposal,
        acceptance,
        source,
      });
      if (!result || result.success === false) {
        throw new Error((result && result.error) || 'Unable to submit your acceptance.');
      }
      const next = result.acceptance || acceptance;
      setSuccess(next);
      onAccepted?.(next, result);
    } catch (err) {
      submittedRef.current = false;
      setSubmitError(err.message || 'Unable to submit your acceptance.');
    } finally {
      setSubmitting(false);
    }
  };

  const heading = success
    ? 'Thank you — we’ve received your confirmation.'
    : step === 1
      ? 'Confirm Your Project'
      : step === 2
        ? 'Confirm Project Details'
        : 'Review and Submit';
  const supporting = success
    ? `Our team will prepare your rental agreement and send it to ${success.agreementEmail}. Your project will be fully confirmed once the required agreement and payment are received.`
    : step === 1
      ? agreementConfirmationCopy(proposal)
      : step === 2
        ? 'Please confirm the information below and update anything that has changed. Details can be adjusted later if needed.'
        : '';
  const venueTimeZone = resolveVenueTimeZone({ proposal, venueAddress: productionDetails.venueAddress });

  return (
    <div role="dialog" aria-modal="true" aria-labelledby="accept-proposal-title" onClick={() => { if (!submitting) onClose?.(); }} style={{ position: 'fixed', inset: 0, zIndex: 11000, backgroundColor: 'rgba(28, 26, 23, 0.38)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <style>{`.accept-date-input::-webkit-calendar-picker-indicator { position: absolute; inset: 0; width: 100%; height: 100%; margin: 0; padding: 0; opacity: 0; cursor: pointer; } .accept-schedule-row { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; } .accept-timing-row { display: grid; grid-template-columns: minmax(0, 1.35fr) minmax(0, 1fr) minmax(0, 1fr); gap: 12px; align-items: start; } @media (max-width: 640px) { .accept-proposal-panel { width: 100% !important; max-width: 100% !important; height: 100% !important; max-height: 100% !important; border-radius: 0 !important; } .accept-schedule-row, .accept-timing-row { grid-template-columns: 1fr; } }`}</style>
      <div className="accept-proposal-panel" onClick={(e) => e.stopPropagation()} style={{ width: '100%', maxWidth: 560, maxHeight: '92vh', overflow: 'auto', backgroundColor: '#fff', borderRadius: 8, border: `1px solid ${RULE}`, boxShadow: '0 12px 40px rgba(28, 26, 23, 0.12)', display: 'flex', flexDirection: 'column' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '24px 28px 0', gap: 16 }}>
          <div>
            <h2 id="accept-proposal-title" style={{ margin: 0, fontFamily: SERIF, fontWeight: 300, fontSize: 26, lineHeight: 1.25, color: INK, letterSpacing: '-0.01em' }}>{heading}</h2>
            {supporting ? <p style={{ margin: '8px 0 0', fontSize: step === 2 ? 13 : 14, lineHeight: 1.55, color: MUTED, fontFamily: FONT, maxWidth: 460, fontWeight: 400 }}>{supporting}</p> : null}
          </div>
          <button type="button" aria-label="Close" onClick={() => !submitting && onClose?.()} style={{ border: 'none', background: 'none', color: MUTED, cursor: submitting ? 'default' : 'pointer', fontSize: 22, lineHeight: 1, padding: 4, fontFamily: FONT }}>×</button>
        </div>
        <div style={{ padding: '22px 28px', flex: 1 }}>
          {success ? (
            <SuccessBody acceptance={success} remit={remit} wirePdfUrl={wirePdfUrl} />
          ) : step === 1 ? (
            <StepAgreement agreementCompanyName={agreementCompanyName} setAgreementCompanyName={setAgreementCompanyName} agreementContactName={agreementContactName} setAgreementContactName={setAgreementContactName} agreementEmail={agreementEmail} setAgreementEmail={setAgreementEmail} preferredPaymentMethod={preferredPaymentMethod} setPreferredPaymentMethod={setPreferredPaymentMethod} errors={step1Errors} remit={remit} wirePdfUrl={wirePdfUrl} />
          ) : step === 2 ? (
            <StepProjectDetails
              productionDetails={productionDetails}
              productionNotes={productionNotes}
              venueTimeZone={venueTimeZone}
              onRegisterTimeValidator={registerTimeValidator}
              onChange={(id, value) => {
                setProductionDetails((prev) => {
                  const next = applyLinkedHardScheduleTimes(prev, { ...prev, [id]: value }, id);
                  next.hardOutDate = inferRolloverDate(next.endDate, next.strikeTime, next.hardOutTime);
                  return next;
                });
              }}
              onNotesChange={setProductionNotes}
              floorPlanError={floorPlanError}
              onFloorPlanStatusChange={(value) => {
                setFloorPlanError('');
                setProductionDetails((prev) => ({ ...prev, floorPlanStatus: value }));
              }}
              existingResources={existingResources.filter((file) => !sessionResources.some((item) => item.id === file.id))}
              sessionResources={sessionResources}
              resourceError={resourceError}
              resourceUploading={resourceUploading}
              onAddResourceFiles={addResourceFiles}
              onRemoveSessionResource={removeSessionResource}
            />
          ) : (
            <StepReview proposal={proposal} fields={fields} productionDetails={productionDetails} originalProduction={originalProduction} productionNotes={productionNotes} />
          )}
          {submitError ? <div style={{ marginTop: 20, fontSize: 13, color: '#7a5348', fontFamily: FONT, lineHeight: 1.5 }}>{submitError}</div> : submitting ? <div style={{ marginTop: 20, fontSize: 13, color: MUTED, fontFamily: FONT, lineHeight: 1.5 }}>Saving your confirmation — this usually takes a few seconds.</div> : null}
        </div>
        <div style={{ padding: '14px 28px 20px', borderTop: `1px solid ${RULE}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          {success ? (
            <>
              <span />
              <button type="button" onClick={onClose} style={primaryBtn()}>Return to Proposal</button>
            </>
          ) : (
            <>
              <button type="button" onClick={() => { if (step === 1) onClose?.(); else setStep((s) => s - 1); }} disabled={submitting} style={{ border: 'none', background: 'none', color: MUTED, fontFamily: FONT, fontSize: 13, cursor: submitting ? 'default' : 'pointer', padding: 0 }}>
                {step === 1 ? 'Cancel' : 'Back'}
              </button>
              {step === 1 ? (
                <button type="button" onClick={goStep2} style={primaryBtn()}>Continue to Confirm →</button>
              ) : step === 2 ? (
                <button type="button" onClick={goToReview} disabled={resourceUploading} style={primaryBtn({ opacity: resourceUploading ? 0.65 : 1, cursor: resourceUploading ? 'wait' : 'pointer' })}>{resourceUploading ? 'Uploading…' : 'Review & Confirm →'}</button>
              ) : (
                <button type="button" onClick={handleSubmit} disabled={submitting} style={primaryBtn({ opacity: submitting ? 0.65 : 1, cursor: submitting ? 'wait' : 'pointer' })}>
                  {submitting ? 'Saving…' : 'Confirm Project'}
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function paymentOptionLabel(method) {
  if (method.id === 'credit_card') return 'Credit Card — 3% processing fee';
  return method.label;
}

function PaymentMethodDetail({ methodId, remit, wirePdfUrl }) {
  if (methodId === 'wire' && wirePdfUrl) {
    return (
      <a href={wirePdfUrl} download="Mayker-Domestic-Wire-Instructions.pdf" target="_blank" rel="noopener noreferrer" style={{ display: 'inline-block', marginTop: 12, fontSize: 13, color: INK, fontFamily: FONT, textDecoration: 'underline', textUnderlineOffset: 3 }}>
        Download Wire Instructions
      </a>
    );
  }
  if (methodId === 'check') {
    return (
      <div style={{ marginTop: 12, fontSize: 13, color: MUTED, fontFamily: FONT, lineHeight: 1.55 }}>
        <div>{remit.company}</div>
        <div>{remit.line1}</div>
        <div>{remit.line2}</div>
      </div>
    );
  }
  if (methodId === 'credit_card') {
    return (
      <div style={{ marginTop: 12, fontSize: 13, color: MUTED, fontFamily: FONT, lineHeight: 1.5 }}>
        3% processing fee applies.
      </div>
    );
  }
  return null;
}

function StepAgreement({ agreementCompanyName, setAgreementCompanyName, agreementContactName, setAgreementContactName, agreementEmail, setAgreementEmail, preferredPaymentMethod, setPreferredPaymentMethod, errors, remit, wirePdfUrl }) {
  return (
    <div>
      <style>{`.accept-contact-row { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; } @media (max-width: 640px) { .accept-contact-row { grid-template-columns: 1fr; } }`}</style>
      <SectionHeading>Agreement Information</SectionHeading>
      <div style={{ display: 'grid', gap: 16, marginBottom: 22 }}>
        <div>
          <FieldLabel htmlFor="accept-company">Agreement Made Out To</FieldLabel>
          <TextInput id="accept-company" value={agreementCompanyName} onChange={setAgreementCompanyName} invalid={Boolean(errors.agreementCompanyName)} />
          <FieldError message={errors.agreementCompanyName} />
        </div>
        <div className="accept-contact-row">
          <div>
            <FieldLabel htmlFor="accept-contact">Agreement Contact</FieldLabel>
            <TextInput id="accept-contact" value={agreementContactName} onChange={setAgreementContactName} invalid={Boolean(errors.agreementContactName)} />
            <FieldError message={errors.agreementContactName} />
          </div>
          <div>
            <FieldLabel htmlFor="accept-email">Agreement Email</FieldLabel>
            <TextInput id="accept-email" type="email" value={agreementEmail} onChange={setAgreementEmail} invalid={Boolean(errors.agreementEmail)} />
            <FieldError message={errors.agreementEmail} />
          </div>
        </div>
      </div>
      <SectionHeading>Preferred Payment Method</SectionHeading>
      <select id="accept-payment" className="mayker-field" value={preferredPaymentMethod} onChange={(e) => setPreferredPaymentMethod(e.target.value)} aria-label="Preferred payment method" style={selectControlStyle(Boolean(errors.preferredPaymentMethod))}>
        <option value="">Select payment method</option>
        {PAYMENT_METHODS.map((method) => (
          <option key={method.id} value={method.id}>{paymentOptionLabel(method)}</option>
        ))}
      </select>
      <PaymentMethodDetail methodId={preferredPaymentMethod} remit={remit} wirePdfUrl={wirePdfUrl} />
      <FieldError message={errors.preferredPaymentMethod} />
    </div>
  );
}

const COI_HELP = 'A Certificate of Insurance may be requested by a client, venue, property manager, landlord, or another party.';
const COI_CHOICES = [
  { value: 'required', label: 'Yes' },
  { value: 'not_required', label: 'No' },
  { value: 'unknown', label: 'Not sure' },
];

const TIMING_HELP = {
  startDate: 'The date Mayker will deliver and install your order.',
  deliveryTime: 'The time Mayker can begin delivery and installation.',
  hardSetTime: 'The time by which Mayker’s installation must be fully complete.',
  endDate: 'The date Mayker will return to remove your order.',
  strikeTime: 'The time Mayker can begin removing your order.',
  hardOutTime: 'The time by which Mayker must be completely out of the venue.',
};

function canUseHover() {
  return typeof window !== 'undefined' && window.matchMedia('(hover: hover) and (pointer: fine)').matches;
}

function FieldInfoTip({ label, children }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const tipId = useId();
  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (event) => { if (wrapRef.current && !wrapRef.current.contains(event.target)) setOpen(false); };
    const onKey = (event) => { if (event.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);
  return (
    <span ref={wrapRef} style={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }} onMouseEnter={() => { if (canUseHover()) setOpen(true); }} onMouseLeave={() => { if (canUseHover()) setOpen(false); }}>
      <button type="button" aria-label={`More information about ${label}`} aria-expanded={open} aria-controls={tipId} onClick={() => setOpen((prev) => !prev)} onFocus={() => setOpen(true)} onBlur={(event) => { if (!wrapRef.current || !wrapRef.current.contains(event.relatedTarget)) setOpen(false); }} style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 18, height: 18, padding: 0, margin: 0, border: 'none', background: 'none', color: '#8a8378', cursor: 'pointer', lineHeight: 1 }}>
        <svg width="13" height="13" viewBox="0 0 16 16" aria-hidden="true" focusable="false">
          <circle cx="8" cy="8" r="6.25" fill="none" stroke="currentColor" strokeWidth="1.2" />
          <circle cx="8" cy="5.2" r="0.85" fill="currentColor" />
          <path d="M8 7.15v4.1" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
        </svg>
      </button>
      {open ? (
        <span id={tipId} role="tooltip" style={{ position: 'absolute', left: 0, top: 'calc(100% + 6px)', zIndex: 2, width: 220, padding: '8px 10px', backgroundColor: '#fff', border: `1px solid ${RULE}`, borderRadius: 6, boxShadow: '0 8px 20px rgba(28, 26, 23, 0.1)', fontSize: 12, fontWeight: 400, lineHeight: 1.45, color: MUTED, fontFamily: FONT }}>
          {children}
        </span>
      ) : null}
    </span>
  );
}

function TitleLabel({ children, htmlFor, info }) {
  return (
    <div style={{ display: 'flex', flexWrap: 'nowrap', alignItems: 'center', minHeight: 20 }}>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
        <label htmlFor={htmlFor} style={{ display: 'block', fontSize: 13, fontWeight: 500, color: INK, fontFamily: FONT, letterSpacing: 0, textTransform: 'none', whiteSpace: 'nowrap' }}>{children}</label>
        {info ? <FieldInfoTip label={children}>{info}</FieldInfoTip> : null}
      </span>
    </div>
  );
}

function HelperText({ children }) {
  if (!children) return null;
  return <div style={{ margin: '4px 0 9px', fontSize: 12, fontWeight: 400, lineHeight: 1.4, color: '#8a8a8a', fontFamily: FONT }}>{children}</div>;
}

const DATE_CHEVRON = `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%236b6b6b' d='M6 9L1 4h10z'/%3E%3C/svg%3E")`;

function DatePicker({ id, value, onChange }) {
  const ymd = parseYmd(value);
  const inputRef = useRef(null);
  const openPicker = () => {
    const el = inputRef.current;
    if (!el) return;
    el.focus();
    if (typeof el.showPicker === 'function') {
      try { el.showPicker(); } catch (_) { /* Safari can throw if the input is not considered visible enough */ }
    }
  };
  return (
    <div style={{ position: 'relative' }}>
      <div aria-hidden="true" style={{ width: '100%', boxSizing: 'border-box', padding: '11px 12px', minHeight: 44, border: `1px solid ${RULE}`, borderRadius: 6, fontSize: 14, fontFamily: FONT, color: ymd ? INK : MUTED, backgroundColor: '#fff', display: 'flex', alignItems: 'center', backgroundImage: DATE_CHEVRON, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 12px center', paddingRight: 32, pointerEvents: 'none' }}>
        {ymd ? formatUsDate(ymd) : 'Select date…'}
      </div>
      <input ref={inputRef} id={id} className="accept-date-input" type="date" value={ymd} onChange={(e) => onChange(e.target.value)} onClick={openPicker} onFocus={openPicker} aria-label={ymd ? formatUsDate(ymd) : 'Select date'} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', opacity: 0.01, cursor: 'pointer', border: 0, background: 'transparent', fontSize: 16 }} />
    </div>
  );
}

function TimeField({ id, value, onChange, onRegisterValidator }) {
  return (
    <MaykerTimeInput
      id={id}
      value={value}
      onChange={onChange}
      outputFormat="clock12"
      onRegisterValidator={onRegisterValidator}
    />
  );
}

function PhoneInput({ id, value, onChange }) {
  return <TextInput id={id} type="tel" value={value} onChange={(next) => onChange(formatPhoneInput(next))} />;
}

function ConfirmField({ id, label, helper, info, children, contextDate, compact }) {
  return (
    <div>
      <TitleLabel htmlFor={id} info={info}>{label}</TitleLabel>
      {helper ? <HelperText>{helper}</HelperText> : <div style={{ height: compact ? 6 : 8 }} />}
      {children}
      {contextDate ? <div style={{ marginTop: 6, fontSize: 12, color: MUTED, fontFamily: FONT }}>{contextDate}</div> : null}
    </div>
  );
}

function SupportingDocumentsSection({
  existingResources,
  sessionResources,
  resourceError,
  resourceUploading,
  onAddResourceFiles,
  onRemoveSessionResource,
}) {
  const inputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const existing = existingResources || [];
  const session = (sessionResources || []).filter((file) => file.purpose !== 'coi');
  const showUploader = uploadOpen || session.length > 0;
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12 }}>
        <SectionHeading>Floor Plan & Supporting Documents</SectionHeading>
        <span style={{ fontSize: 11, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#8a8378', fontFamily: FONT }}>Optional</span>
      </div>
      <HelperText>Upload any current floor plans or other documents that may help our team prepare for your project.</HelperText>
      {existing.length ? (
        <div style={{ marginBottom: showUploader || session.length ? 12 : 0 }}>
          <div style={{ fontSize: 13, fontWeight: 500, color: INK, fontFamily: FONT, marginBottom: 6 }}>Already on file</div>
          <div style={{ display: 'grid', gap: 4 }}>
            {existing.map((file) => (
              <div key={file.id} style={{ fontSize: 13, color: INK, fontFamily: FONT, lineHeight: 1.4 }}>{file.name}</div>
            ))}
          </div>
        </div>
      ) : null}
      {showUploader ? (
        <>
          <div
            role="button"
            tabIndex={0}
            onClick={() => inputRef.current && inputRef.current.click()}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                inputRef.current && inputRef.current.click();
              }
            }}
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(e) => { e.preventDefault(); setDragOver(false); onAddResourceFiles?.(e.dataTransfer.files); }}
            style={{ border: `1px dashed ${dragOver ? INK : RULE}`, borderRadius: 6, padding: '14px 16px', textAlign: 'center', fontSize: 13, color: MUTED, fontFamily: FONT, backgroundColor: dragOver ? '#fafaf8' : '#fff', cursor: resourceUploading ? 'wait' : 'pointer' }}
          >
            <input ref={inputRef} type="file" multiple accept={ACCEPTANCE_RESOURCE_ACCEPT} hidden onChange={(e) => { onAddResourceFiles?.(e.target.files); e.target.value = ''; }} />
            {resourceUploading ? 'Uploading…' : 'Drag and drop files here, or browse'}
          </div>
          {session.length ? (
            <div style={{ display: 'grid', gap: 8, marginTop: 12 }}>
              {session.map((file) => (
                <div key={file.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                  <div style={{ fontSize: 14, color: INK, fontFamily: FONT, lineHeight: 1.4, minWidth: 0 }}>{file.name}</div>
                  <button type="button" onClick={() => onRemoveSessionResource?.(file)} style={{ border: 'none', background: 'none', color: MUTED, fontSize: 12, fontFamily: FONT, cursor: 'pointer', padding: 0, flexShrink: 0 }}>Remove</button>
                </div>
              ))}
            </div>
          ) : null}
        </>
      ) : (
        <button type="button" onClick={() => setUploadOpen(true)} style={{ border: 'none', background: 'none', padding: 0, color: INK, fontFamily: FONT, fontSize: 13, cursor: 'pointer', textDecoration: 'underline', textUnderlineOffset: 3 }}>
          + Add documents
        </button>
      )}
      {resourceError ? <div style={{ marginTop: 8, fontSize: 12, color: '#7a5348', fontFamily: FONT }}>{resourceError}</div> : null}
    </div>
  );
}

function CompactChoice({ id, name, value, options, onChange }) {
  return (
    <div role="radiogroup" aria-labelledby={id} style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
      {options.map((option) => {
        const selected = value === option.value;
        const optionId = `${name}-${option.value}`;
        return (
          <label key={option.value} htmlFor={optionId} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 12px', minHeight: 40, border: `1px solid ${selected ? INK : RULE}`, borderRadius: 6, fontSize: 14, fontFamily: FONT, color: INK, backgroundColor: selected ? '#f7f4ee' : '#fff', cursor: 'pointer' }}>
            <input id={optionId} type="radio" name={name} value={option.value} checked={selected} onChange={() => onChange(option.value)} style={{ margin: 0 }} />
            {option.label}
          </label>
        );
      })}
    </div>
  );
}

function CoiUploadField({ files, resourceError, resourceUploading, onAddResourceFiles, onRemoveSessionResource }) {
  const inputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);
  return (
    <div>
      <TitleLabel htmlFor="accept-coi-docs">Upload COI Requirements</TitleLabel>
      <HelperText>Optional. If you have a requirements document, uploading it is the easiest option.</HelperText>
      <div
        role="button"
        tabIndex={0}
        onClick={() => inputRef.current && inputRef.current.click()}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            inputRef.current && inputRef.current.click();
          }
        }}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => { e.preventDefault(); setDragOver(false); onAddResourceFiles?.(e.dataTransfer.files, { purpose: 'coi' }); }}
        style={{ border: `1px dashed ${dragOver ? INK : RULE}`, borderRadius: 6, padding: '12px 14px', textAlign: 'center', fontSize: 13, color: MUTED, fontFamily: FONT, backgroundColor: dragOver ? '#fafaf8' : '#fff', cursor: resourceUploading ? 'wait' : 'pointer' }}
      >
        <input ref={inputRef} id="accept-coi-docs" type="file" multiple accept={ACCEPTANCE_RESOURCE_ACCEPT} hidden onChange={(e) => { onAddResourceFiles?.(e.target.files, { purpose: 'coi' }); e.target.value = ''; }} />
        {resourceUploading ? 'Uploading…' : 'Upload a requirements document or sample certificate'}
      </div>
      {files.length ? (
        <div style={{ display: 'grid', gap: 8, marginTop: 10 }}>
          {files.map((file) => (
            <div key={file.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
              <div style={{ fontSize: 14, color: INK, fontFamily: FONT, lineHeight: 1.4, minWidth: 0 }}>{file.name}</div>
              <button type="button" onClick={() => onRemoveSessionResource?.(file)} style={{ border: 'none', background: 'none', color: MUTED, fontSize: 12, fontFamily: FONT, cursor: 'pointer', padding: 0, flexShrink: 0 }}>Remove</button>
            </div>
          ))}
        </div>
      ) : null}
      {resourceError ? <div style={{ marginTop: 8, fontSize: 12, color: '#7a5348', fontFamily: FONT }}>{resourceError}</div> : null}
    </div>
  );
}

function StepProjectDetails({ productionDetails, productionNotes, venueTimeZone, onChange, onRegisterTimeValidator, onNotesChange, floorPlanError, onFloorPlanStatusChange, existingResources, sessionResources, resourceError, resourceUploading, onAddResourceFiles, onRemoveSessionResource }) {
  const hardSetDate = inferRolloverDate(productionDetails.startDate, productionDetails.deliveryTime, productionDetails.hardSetTime);
  const hardOutDate = inferRolloverDate(productionDetails.endDate, productionDetails.strikeTime, productionDetails.hardOutTime);
  const showHardSetDate = hardSetDate && parseYmd(productionDetails.startDate) && hardSetDate !== parseYmd(productionDetails.startDate);
  const showHardOutDate = hardOutDate && parseYmd(productionDetails.endDate) && hardOutDate !== parseYmd(productionDetails.endDate);
  const scheduleZoneLabel = formatScheduleTimeZoneLabel(venueTimeZone, productionDetails.startDate || productionDetails.endDate);
  return (
    <div>
      <SectionHeading>Event Details</SectionHeading>
      <div style={{ display: 'grid', gap: 22 }}>
        <ConfirmField id="accept-venueName" label="Venue">
          <TextInput id="accept-venueName" value={productionDetails.venueName || ''} onChange={(value) => onChange('venueName', value)} />
        </ConfirmField>
        <ConfirmField id="accept-venueAddress" label="Venue Address">
          <TextInput id="accept-venueAddress" value={productionDetails.venueAddress || ''} onChange={(value) => onChange('venueAddress', value)} />
        </ConfirmField>
        <div className="accept-schedule-row">
          <ConfirmField id="accept-dayOfContact" label="Day-of Contact">
            <TextInput id="accept-dayOfContact" value={productionDetails.dayOfContact || ''} onChange={(value) => onChange('dayOfContact', value)} />
          </ConfirmField>
          <ConfirmField id="accept-dayOfContactPhone" label="Mobile Number">
            <PhoneInput id="accept-dayOfContactPhone" value={productionDetails.dayOfContactPhone || ''} onChange={(value) => onChange('dayOfContactPhone', value)} />
          </ConfirmField>
        </div>
        <ConfirmField id="accept-coiStatus" label="Certificate of Insurance (COI)" info={COI_HELP}>
          <div style={{ fontSize: 13, color: INK, fontFamily: FONT, marginBottom: 8 }}>Is a Certificate of Insurance (COI) required for this project?</div>
          <CompactChoice id="accept-coiStatus" name="accept-coiStatus" value={productionDetails.coiStatus || ''} options={COI_CHOICES} onChange={(value) => onChange('coiStatus', value)} />
          {productionDetails.coiStatus === 'unknown' ? (
            <div style={{ marginTop: 8, fontSize: 12, color: MUTED, fontFamily: FONT, lineHeight: 1.4 }}>No problem — you can send COI requirements to us later if needed.</div>
          ) : null}
        </ConfirmField>
        {productionDetails.coiStatus === 'required' ? (
          <div style={{ display: 'grid', gap: 16 }}>
            <ConfirmField id="accept-coiCertificateHolderName" label="Certificate Holder Name" helper="Optional">
              <TextInput id="accept-coiCertificateHolderName" value={productionDetails.coiCertificateHolderName || ''} onChange={(value) => onChange('coiCertificateHolderName', value)} />
            </ConfirmField>
            <ConfirmField id="accept-coiCertificateHolderAddress" label="Certificate Holder Address" helper="Optional">
              <textarea id="accept-coiCertificateHolderAddress" className="mayker-field" value={productionDetails.coiCertificateHolderAddress || ''} onChange={(e) => onChange('coiCertificateHolderAddress', e.target.value)} rows={2} placeholder="Street, city, state, ZIP" style={{ width: '100%', boxSizing: 'border-box', minHeight: 58, resize: 'vertical', padding: '11px 12px', border: `1px solid ${RULE}`, borderRadius: 6, fontSize: 14, fontFamily: FONT, color: INK, backgroundColor: '#fff', outline: 'none', lineHeight: 1.5 }} />
            </ConfirmField>
            <CoiUploadField
              files={(sessionResources || []).filter((file) => file.purpose === 'coi')}
              resourceError={resourceError}
              resourceUploading={resourceUploading}
              onAddResourceFiles={onAddResourceFiles}
              onRemoveSessionResource={onRemoveSessionResource}
            />
            <ConfirmField id="accept-coiNotes" label="Additional Notes" helper="Optional">
              <textarea id="accept-coiNotes" className="mayker-field" value={productionDetails.coiNotes || ''} onChange={(e) => onChange('coiNotes', e.target.value)} rows={3} placeholder="Anything else that may help us request the certificate…" style={{ width: '100%', boxSizing: 'border-box', minHeight: 78, resize: 'vertical', padding: '11px 12px', border: `1px solid ${RULE}`, borderRadius: 6, fontSize: 14, fontFamily: FONT, color: INK, backgroundColor: '#fff', outline: 'none', lineHeight: 1.5 }} />
            </ConfirmField>
          </div>
        ) : null}
      </div>
      {scheduleZoneLabel ? (
        <div style={{ margin: '22px 0 10px', fontSize: 12, color: MUTED, fontFamily: FONT }}>{scheduleZoneLabel}</div>
      ) : (
        <div style={{ height: 22 }} />
      )}
      <SectionHeading>Delivery + Installation</SectionHeading>
      <div className="accept-timing-row">
        <ConfirmField id="accept-startDate" label="Delivery Date" info={TIMING_HELP.startDate} compact>
          <DatePicker id="accept-startDate" value={productionDetails.startDate} onChange={(value) => onChange('startDate', value)} />
        </ConfirmField>
        <ConfirmField id="accept-deliveryTime" label="Delivery Time" info={TIMING_HELP.deliveryTime} compact>
          <TimeField id="accept-deliveryTime" value={productionDetails.deliveryTime} onChange={(value) => onChange('deliveryTime', value)} onRegisterValidator={onRegisterTimeValidator} />
        </ConfirmField>
        <ConfirmField id="accept-hardSetTime" label="Hard Set Time" info={TIMING_HELP.hardSetTime} compact contextDate={showHardSetDate ? formatContextDate(hardSetDate) : ''}>
          <TimeField id="accept-hardSetTime" value={productionDetails.hardSetTime} onChange={(value) => onChange('hardSetTime', value)} onRegisterValidator={onRegisterTimeValidator} />
        </ConfirmField>
      </div>
      <div style={{ height: 22 }} />
      <SectionHeading>Strike + Removal</SectionHeading>
      <div className="accept-timing-row">
        <ConfirmField id="accept-endDate" label="Strike Date" info={TIMING_HELP.endDate} compact>
          <DatePicker id="accept-endDate" value={productionDetails.endDate} onChange={(value) => onChange('endDate', value)} />
        </ConfirmField>
        <ConfirmField id="accept-strikeTime" label="Strike Time" info={TIMING_HELP.strikeTime} compact>
          <TimeField id="accept-strikeTime" value={productionDetails.strikeTime} onChange={(value) => onChange('strikeTime', value)} onRegisterValidator={onRegisterTimeValidator} />
        </ConfirmField>
        <ConfirmField id="accept-hardOutTime" label="Hard Out Time" info={TIMING_HELP.hardOutTime} compact contextDate={showHardOutDate ? formatContextDate(hardOutDate) : ''}>
          <TimeField id="accept-hardOutTime" value={productionDetails.hardOutTime} onChange={(value) => onChange('hardOutTime', value)} onRegisterValidator={onRegisterTimeValidator} />
        </ConfirmField>
      </div>
      <div style={{ height: 22 }} />
      <TitleLabel htmlFor="accept-productionNotes">Additional Notes</TitleLabel>
      <HelperText>Share any additional timing, access, or production details our team should know.</HelperText>
      <textarea id="accept-productionNotes" className="mayker-field" value={productionNotes} onChange={(e) => onNotesChange(e.target.value)} rows={3} placeholder="Add any helpful context…" style={{ width: '100%', boxSizing: 'border-box', minHeight: 78, resize: 'vertical', padding: '11px 12px', border: `1px solid ${RULE}`, borderRadius: 6, fontSize: 14, fontFamily: FONT, color: INK, backgroundColor: '#fff', outline: 'none', lineHeight: 1.5 }} />
      <div style={{ height: 22 }} />
      <div id="accept-floor-plan-status" style={{ marginBottom: 22 }}>
        <div id="accept-floor-plan-status-label" style={{ fontSize: 13, color: INK, fontFamily: FONT, marginBottom: 6 }}>
          Is a floor plan required for this project?
        </div>
        <CompactChoice
          id="accept-floor-plan-status-label"
          name="accept-floor-plan-status"
          value={productionDetails.floorPlanStatus || ''}
          options={FLOOR_PLAN_ANSWER_OPTIONS}
          onChange={onFloorPlanStatusChange}
        />
        <FieldError message={floorPlanError} />
      </div>
      <SupportingDocumentsSection
        existingResources={existingResources}
        sessionResources={sessionResources}
        resourceError={resourceError}
        resourceUploading={resourceUploading}
        onAddResourceFiles={onAddResourceFiles}
        onRemoveSessionResource={onRemoveSessionResource}
      />
    </div>
  );
}

function reviewPaymentSummary(methodId) {
  if (methodId === 'credit_card') {
    return (
      <>
        <div>Credit Card</div>
        <div style={{ color: MUTED }}>3% processing fee applies.</div>
      </>
    );
  }
  return paymentMethodLabel(methodId);
}

function reviewChangeLine(change) {
  const previous = String((change && change.original) || '').trim();
  const next = String((change && change.updated) || '').trim();
  if (previous && next) return `${previous} → ${next}`;
  return next || previous;
}

function reviewProjectTitle(proposal, fields, productionDetails) {
  const client = stripClientNameVersion(proposal?.clientName || (fields && fields.agreementCompanyName));
  const venue = String((productionDetails && productionDetails.venueName) || (proposal && proposal.venueName) || '').trim();
  const label = stripClientNameVersion((proposal && (proposal.projectLabel || proposal.label)) || '');
  if (venue) return venue;
  if (label && label !== client) return label;
  return '';
}

function StepReview({ proposal, fields, productionDetails, originalProduction, productionNotes }) {
  const eventName = reviewProjectTitle(proposal, fields, productionDetails);
  const projectDates = formatEventDateRange({
    startDate: (productionDetails && productionDetails.startDate) || proposal?.startDate,
    endDate: (productionDetails && productionDetails.endDate) || proposal?.endDate,
  });
  const detailChanges = collectProductionChanges(originalProduction, productionDetails, [], { productionNotes }).filter((change) => !['coiStatus', 'coiNotes', 'coiCertificateHolderName', 'coiCertificateHolderAddress'].includes(change.field));
  return (
    <div>
      <ReviewRow label="Rental Agreement">{fields.agreementCompanyName}</ReviewRow>
      <ReviewRow label="Agreement Contact">
        <div>{fields.agreementContactName}</div>
        <div>{fields.agreementEmail}</div>
      </ReviewRow>
      <ReviewRow label="Preferred Payment">{reviewPaymentSummary(fields.preferredPaymentMethod)}</ReviewRow>
      {coiStatusLabel(productionDetails && productionDetails.coiStatus) ? (
        <ReviewRow label="Certificate of Insurance">
          <div>{coiStatusLabel(productionDetails.coiStatus)}</div>
          {productionDetails.coiStatus === 'required' && String(productionDetails.coiCertificateHolderName || '').trim() ? (
            <div style={{ color: MUTED }}>{productionDetails.coiCertificateHolderName}</div>
          ) : null}
          {productionDetails.coiStatus === 'required' && String(productionDetails.coiCertificateHolderAddress || '').trim() ? (
            <div style={{ color: MUTED }}>{productionDetails.coiCertificateHolderAddress}</div>
          ) : null}
          {productionDetails.coiStatus === 'required' && String(productionDetails.coiNotes || '').trim() ? (
            <div style={{ color: MUTED }}>{productionDetails.coiNotes}</div>
          ) : null}
        </ReviewRow>
      ) : null}
      <ReviewRow label="Project">
        {eventName ? <div>{eventName}</div> : null}
        {projectDates ? <div>{projectDates}</div> : null}
      </ReviewRow>
      {productionDetails && productionDetails.floorPlanStatus === 'required' ? (
        <ReviewRow label="Floor Plan">A floor plan is required</ReviewRow>
      ) : null}
      {productionDetails && productionDetails.floorPlanStatus === 'not_required' ? (
        <ReviewRow label="Floor Plan">No floor plan is required</ReviewRow>
      ) : null}
      {detailChanges.length ? (
        <ReviewRow label="Updated Details">
          {detailChanges.map((change) => (
            <div key={change.field} style={{ marginBottom: 4 }}>
              <div style={{ fontSize: 13, color: MUTED }}>{change.label}</div>
              <div>{reviewChangeLine(change)}</div>
            </div>
          ))}
        </ReviewRow>
      ) : null}
    </div>
  );
}

function SuccessBody({ acceptance, remit, wirePdfUrl }) {
  return (
    <div>
      {acceptance.preferredPaymentMethod === 'wire' && wirePdfUrl ? (
        <a href={wirePdfUrl} download="Mayker-Domestic-Wire-Instructions.pdf" target="_blank" rel="noopener noreferrer" style={{ display: 'inline-block', marginBottom: 16, fontSize: 13, color: INK, fontFamily: FONT, textDecoration: 'underline', textUnderlineOffset: 3 }}>Download Wire Instructions</a>
      ) : null}
      {acceptance.preferredPaymentMethod === 'check' ? (
        <div style={{ fontSize: 14, color: INK, fontFamily: FONT, lineHeight: 1.65 }}>
          <div style={{ fontSize: 11, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#8a8378', marginBottom: 8 }}>Remittance address</div>
          <div>{remit.company}</div>
          <div>{remit.line1}</div>
          <div>{remit.line2}</div>
        </div>
      ) : null}
    </div>
  );
}
